import { TraceLoggingHelpers } from '../../helpers/trace-logging-helpers';
import { TIDataPointToToIndiciatorPointMapper } from "../../mappers/data-point/ti-chart-point-to-ti-indicator-point.mapper";
import { TIIndicatorPointToHighChartIndicatorPointMapper } from '../../mappers/data-point/ti-indicator-point-to-highchart-indicator-point.mapper';
import { TIIndicatorDataModel } from "../../models/data-point/ti-indicator-data.model";
import { TIIndicatorPointModel } from '../../models/data-point/ti-indicator-point.model';
import { IndicatorType } from "../../models/enum/indicator-type.enum";
import { IComponent } from "../component.interface";
import { TiChartSeriesComponent } from "../ti-chart-series.component";
import { BBandsIndicatorComponent } from './bbands-indicator.component';
import { DCIndicatorComponent } from './dc-indicator.component';
import { EMACloudIndicatorComponent } from './ema-cloud-indicator.component';
import { KeltnerIndicatorComponent } from './keltner-indicator.component';
import { MACDIndicatorComponent } from './macd-indicator.component';

export class IndicatorsComponent implements IComponent {
    public id = '';

    private tiChartSeries: TiChartSeriesComponent;
    private macdIndicator: MACDIndicatorComponent;
    private dcIndicator: DCIndicatorComponent;
    private bbandsIndicator: BBandsIndicatorComponent;
    private keltnerIndicator: KeltnerIndicatorComponent;
    private emaCloudIndicator: EMACloudIndicatorComponent;

    public canUpdateEMACloud = false;

    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
        this.macdIndicator = new MACDIndicatorComponent(tiChartSeries);
        this.dcIndicator = new DCIndicatorComponent(tiChartSeries);
        this.bbandsIndicator = new BBandsIndicatorComponent(tiChartSeries);
        this.keltnerIndicator = new KeltnerIndicatorComponent(tiChartSeries);
        this.emaCloudIndicator = new EMACloudIndicatorComponent(tiChartSeries);
    }

    public setData(initiailizeData: Array<TIIndicatorDataModel>) {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.highStockChart?.series || this.tiChartSeries.highStockChart?.series?.length <= 1) {
            return;
        }

        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        for (const indicatorOption of allIndicatorOptions) {
            if (indicatorOption.highChartIndicator) {
                continue;
            }

            if (indicatorOption.value == IndicatorType.EMACloud) {
                const indicatorData = initiailizeData.find((x: TIIndicatorDataModel) => x.name == indicatorOption.name);
                this.emaCloudIndicator.setData(indicatorData);    
            }
        }
    }

    public clearData(initiailizeData: Array<TIIndicatorDataModel>) {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.highStockChart?.series || this.tiChartSeries.highStockChart?.series?.length <= 1) {
            return;
        }

        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        for (const indicatorOption of allIndicatorOptions) {
            if (indicatorOption.highChartIndicator) {
                continue;
            }

            if (indicatorOption.value == IndicatorType.EMACloud) {
                const indicatorData = initiailizeData.find((x: TIIndicatorDataModel) => x.name == indicatorOption.name);
                this.emaCloudIndicator.clearData(indicatorData);    
            }
        }
    }

    public removeEMACloudSeries(initiailizeData: Array<TIIndicatorDataModel>) {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.highStockChart?.series || this.tiChartSeries.highStockChart?.series?.length <= 1) {
            return;
        }

        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        for (const indicatorOption of allIndicatorOptions) {
            if (indicatorOption.highChartIndicator) {
                continue;
            }

            if (indicatorOption.value == IndicatorType.EMACloud) {
                const indicatorData = initiailizeData.find((x: TIIndicatorDataModel) => x.name == indicatorOption.name);
                this.emaCloudIndicator.remove(indicatorData);    
            }
        }
    }

    public hasEMACloudSeries(initiailizeData: Array<TIIndicatorDataModel>): boolean {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.highStockChart?.series || this.tiChartSeries.highStockChart?.series?.length <= 1) {
            return false;
        }

        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        let valid = true;
        for (const indicatorOption of allIndicatorOptions) {
            if (indicatorOption.highChartIndicator) {
                continue;
            }

            if (indicatorOption.value == IndicatorType.EMACloud) {
                const indicatorData = initiailizeData.find((x: TIIndicatorDataModel) => x.name == indicatorOption.name);
                const hasSeries = this.emaCloudIndicator.hasSeries(indicatorData);    
                if (!hasSeries) {
                    valid = false;
                }
            }
        }

        return valid;
    }

    public updateIndicatorsData(initiailizeData: Array<TIIndicatorDataModel>) {
        if (!this.tiChartSeries.chartStyleSettings || !this.tiChartSeries.highStockChart?.series || this.tiChartSeries.highStockChart?.series?.length <= 1) {
            return;
        }

        this.tiChartSeries.ready = false;
        const mappedIndicatorsData = TIDataPointToToIndiciatorPointMapper.map(initiailizeData, this.tiChartSeries.tiChartPoints);
        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        for (const indicatorOption of allIndicatorOptions) {
            if (indicatorOption.highChartIndicator) {
                continue;
            }
            
            // TraceLoggingHelpers.log('update indicators ', indicatorOption)
            const indicatorData = mappedIndicatorsData.find((x: TIIndicatorDataModel) => x.name == indicatorOption.name);
            const tiIndicatorDataIndex = this.tiChartSeries.tiIndicatorsData.findIndex((x: TIIndicatorDataModel) => x.name == indicatorOption.name);

            const currentTiIndicatorsData = this.tiChartSeries.tiIndicatorsData[tiIndicatorDataIndex];
            if (tiIndicatorDataIndex <= -1 || !currentTiIndicatorsData.points.length || !indicatorData || !indicatorData.points.length) {
                continue;
            }

            const data = indicatorData.points?.length ? indicatorData.points[indicatorData.points.length - 1] : null;
            if (!data) {
                continue;
            }

            const lastXValue = currentTiIndicatorsData.points[currentTiIndicatorsData.points.length - 1].xValue;
            if (!lastXValue || lastXValue > data.xValue) {
                continue;
            }

            const seriesName = indicatorOption.name;
            let series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name == seriesName);
            if (indicatorOption.value == IndicatorType.MACD) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('MACD_LINE') >= 0);
            } else if (indicatorOption.value == IndicatorType.DonchianChanel) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('DC T') >= 0);
            } else if (indicatorOption.value == IndicatorType.BBands) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('BB_T') >= 0);
            } else if (indicatorOption.value == IndicatorType.KeltnerChannel) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('KELTNER_T') >= 0);
            } else if (indicatorOption.value == IndicatorType.EMACloud) {
                if (this.canUpdateEMACloud) {
                    this.emaCloudIndicator.updateIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, lastXValue == data.xValue);
                }

                continue;
            }

            if (!series) {
                continue;
            }

            const needZone =  currentTiIndicatorsData.needHighChartZone(series.type);

            if (lastXValue == data.xValue) {
                // TraceLoggingHelpers.log('Update indicator point', data);
                const lastChartBar = series.points.find((x: any) => x.x == data.xValue);
                const tiIndicatorDataPointIndex = currentTiIndicatorsData.points.findIndex(x => x.xValue == data.xValue);
                // update to main ti indicator data points
                if (tiIndicatorDataPointIndex > -1) {
                    currentTiIndicatorsData.points[tiIndicatorDataPointIndex] = data;
                }

                if (!lastChartBar) {
                    const dataPoints = TIIndicatorPointToHighChartIndicatorPointMapper.map(currentTiIndicatorsData, indicatorOption.value, needZone);
                    // TraceLoggingHelpers.log('setData ')
                    series.setData(dataPoints.points, !needZone, false, true);
                    if (needZone) {
                        currentTiIndicatorsData.zones = dataPoints.zones;
                    }
                } else {
                    const lastHighChartDataPoint = TIIndicatorPointToHighChartIndicatorPointMapper.toHighChartIndicatorPoint(indicatorOption.value, data, currentTiIndicatorsData);
                    lastChartBar.update(lastHighChartDataPoint, false);

                    if (needZone) {
                        const existingZoneIndex = currentTiIndicatorsData.zones.findIndex((x: any) => x.value == data.xValue);
                        if (existingZoneIndex > -1) {
                            const lastHighChartZoneDataPoint = TIIndicatorPointToHighChartIndicatorPointMapper.toHighChartZone(data, currentTiIndicatorsData);
                            currentTiIndicatorsData.zones[existingZoneIndex] = lastHighChartZoneDataPoint;
                        }
                    }
                }

                // update children indicators data points
                if (indicatorOption.value == IndicatorType.MACD) {
                    this.macdIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, true);
                } else if (indicatorOption.value == IndicatorType.DonchianChanel) {
                    this.dcIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, true);
                } else if (indicatorOption.value == IndicatorType.BBands) {
                    this.bbandsIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, true);
                } else if (indicatorOption.value == IndicatorType.KeltnerChannel) {
                    this.keltnerIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, true);
                }
            } else if (data.xValue > lastXValue) {
                // TraceLoggingHelpers.log('Add new indicator point', data);
                let highChartDataPoint = TIIndicatorPointToHighChartIndicatorPointMapper.toHighChartIndicatorPoint(indicatorOption.value, data, currentTiIndicatorsData);
                if (!this.tiChartSeries.isCurrentBarVisible()) {
                    series.addPoint(highChartDataPoint, false);
                } else {
                    series.addPoint(highChartDataPoint, true);
                }

                if (needZone) {
                    const lastHighChartZoneDataPoint = TIIndicatorPointToHighChartIndicatorPointMapper.toHighChartZone(data, currentTiIndicatorsData);
                    currentTiIndicatorsData.zones.push(lastHighChartZoneDataPoint);
                }

                // update to main ti indicator data points
                currentTiIndicatorsData.points.push(data);

                // add children indicators data points
                if (indicatorOption.value == IndicatorType.MACD) {
                    this.macdIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, false);
                } else if (indicatorOption.value == IndicatorType.DonchianChanel) {
                    this.dcIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, false);
                } else if (indicatorOption.value == IndicatorType.BBands) {
                    this.bbandsIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, false);
                } else if (indicatorOption.value == IndicatorType.KeltnerChannel) {
                    this.keltnerIndicator.updateChildrenIndicators(indicatorData, indicatorOption, tiIndicatorDataIndex, false);
                }
            }

            this.tiChartSeries.lastIndicatorXValue = data.xValue;
            if (needZone) {
                series.update({
                    zones: currentTiIndicatorsData.zones,
                    type: series.type == 'line' ? 'line' : 'spline',
                    zoneAxis: 'x'
                });
            }
        }

        this.tiChartSeries.setExtreamToMinMax();
        this.tiChartSeries.setChartReady();
    }

    public updateHistoryChartIndicatorData(data: Array<TIIndicatorDataModel>) {
        this.tiChartSeries.ready = false;
        this.syncHistoryChartIndicator(data);
        if (!this.tiChartSeries.highStockChart || this.tiChartSeries.highStockChart?.series?.length <= 1) {
            return;
        }

        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        for (const indicatorOption of allIndicatorOptions) {
            const seriesName = indicatorOption.name;
            let series: any = this.tiChartSeries.highStockChart.series.find((x: any) => x.name == seriesName);
            if (indicatorOption.highChartIndicator) {
                continue;
            }


            const indicatorData = this.tiChartSeries.tiIndicatorsData.find((x: TIIndicatorDataModel) => x.name == indicatorOption.name);
            if (!indicatorData?.points.length) {
                return;
            }

            if (indicatorOption.value == IndicatorType.MACD) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('MACD_LINE') >= 0);
            } else if (indicatorOption.value == IndicatorType.DonchianChanel) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('DC B') >= 0);
            } else if (indicatorOption.value == IndicatorType.BBands) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('BB_T') >= 0);
            } else if (indicatorOption.value == IndicatorType.KeltnerChannel) {
                series = this.tiChartSeries.highStockChart.series.find((x: any) => x.name.indexOf('KELTNER_T') >= 0);
            } else if (indicatorOption.value == IndicatorType.EMACloud) {
                if (this.canUpdateEMACloud) {
                    this.emaCloudIndicator.updateHistory(indicatorData, indicatorOption);
                }
                continue;
            }

            if (!series) continue;
            const firstPoint = series.xData[0];

            TraceLoggingHelpers.log('update history indicator for ', indicatorOption.text)

            const needZone = indicatorData.needHighChartZone(series.type);
            let serieZones = indicatorData.zones;
            for (let j = indicatorData.points.length - 1; j > 0; j--) {
                const indicatorDataPoint = indicatorData.points[j];
                if (indicatorDataPoint.timestamp && indicatorDataPoint.xValue < firstPoint) {
                    const point = TIIndicatorPointToHighChartIndicatorPointMapper.toHighChartIndicatorPoint(indicatorOption.value, indicatorDataPoint, indicatorData);
                    series.addPoint(point, false);
                    if (needZone) {
                        const zonePoint = TIIndicatorPointToHighChartIndicatorPointMapper.toHighChartZone(indicatorDataPoint, indicatorData);
                        serieZones.push(zonePoint);
                    }
                }
            }

            if (needZone && serieZones.length) {
                series.update({
                    zones: serieZones,
                    type: series.type == 'line' ? 'line' : 'spline',
                    zoneAxis: 'x'
                });
            }

            if (indicatorOption.value == IndicatorType.MACD) {
                this.macdIndicator.updateHistoryChildrenIndicators(indicatorData, indicatorOption, firstPoint);
            } else if (indicatorOption.value == IndicatorType.DonchianChanel) {
                this.dcIndicator.updateHistoryChildrenIndicators(indicatorData, indicatorOption, firstPoint);
            } else if (indicatorOption.value == IndicatorType.BBands) {
                this.bbandsIndicator.updateHistoryChildrenIndicators(indicatorData, indicatorOption, firstPoint);
            } else if (indicatorOption.value == IndicatorType.KeltnerChannel) {
                this.keltnerIndicator.updateHistoryChildrenIndicators(indicatorData, indicatorOption, firstPoint);
            }

            TraceLoggingHelpers.log('update history indicator series ', series)
        }

        this.tiChartSeries.setExtremes(this.tiChartSeries.extreamMin, this.tiChartSeries.extreamMax);
        this.tiChartSeries.redrawHighChartIndicator();
        this.tiChartSeries.setChartReady();
    }

    private syncHistoryChartIndicator(initiailizeData: Array<TIIndicatorDataModel>) {
        for (const data of initiailizeData) {
            const primaryIndicator = this.tiChartSeries.tiIndicatorsData.find((x: TIIndicatorDataModel) => x.name == data.name);
            for (const point of data.points) {
                TIDataPointToToIndiciatorPointMapper.mapWithTIDataPoint(point, this.tiChartSeries.tiChartPoints);
                primaryIndicator?.points?.unshift(point);
            }

            if (primaryIndicator) {
                primaryIndicator.points.sort((x: TIIndicatorPointModel, y: TIIndicatorPointModel) => { return x.xValue - y.xValue });
            }

            for (const child of data.childIndicators) {
                const primaryChildIndicator = primaryIndicator?.childIndicators.find((x: TIIndicatorDataModel) => x.name == child.name);
                for (const point of child.points) {
                    TIDataPointToToIndiciatorPointMapper.mapWithTIDataPoint(point, this.tiChartSeries.tiChartPoints);
                    primaryChildIndicator?.points?.unshift(point);
                    primaryChildIndicator?.points?.sort((x: TIIndicatorPointModel, y: TIIndicatorPointModel) => { return x.xValue - y.xValue });
                }
            }
        }
    }
}